/**
 * Authentication Context and Provider
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5, 13.6, 19.4
 *
 * Provides authentication state and methods throughout the application.
 * Uses httpOnly cookies for token storage (set by the server).
 * Integrates with audit logging for auth events.
 */

import { createContext, useContext, useCallback, useEffect, useState, useRef, type ReactNode } from 'react';
import type { User, AuthContext as AuthContextType } from '@course-authoring/shared-types';
import {
  setAuditContext,
  logUserLogin,
  logUserLogout,
  logTokenRefresh,
} from '../api/auditService';
import { SESSION_EXPIRED_EVENT } from '../api/apiUtils';

const TOKEN_REFRESH_INTERVAL = 14 * 60 * 1000; // 14 minutes

interface LoginResponse {
  user: User;
}

interface AuthProviderProps {
  children: ReactNode;
  apiBaseUrl?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DEFAULT_API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export function AuthProvider({ children, apiBaseUrl = DEFAULT_API_BASE_URL }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const refreshToken = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setUser(null);
        setAuditContext(null);
        throw new Error('Session expired. Please log in again.');
      }

      const data: LoginResponse = await response.json();
      setUser(data.user);

      setAuditContext({
        userId: data.user.id,
      });

      logTokenRefresh(data.user.id);

      scheduleTokenRefresh();
    } catch (error) {
      setUser(null);
      setAuditContext(null);
      throw error;
    }
  }, [apiBaseUrl]);

  const scheduleTokenRefresh = useCallback(() => {
    clearRefreshTimer();

    refreshTimerRef.current = setTimeout(() => {
      refreshToken().catch(console.error);
    }, TOKEN_REFRESH_INTERVAL);
  }, [clearRefreshTimer, refreshToken]);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const response = await fetch(`${apiBaseUrl}/auth/login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Login failed' }));
      throw new Error(error.message || 'Invalid credentials');
    }

    const data: LoginResponse = await response.json();

    setUser(data.user);

    setAuditContext({
      userId: data.user.id,
    });

    logUserLogin(data.user.id, { email });

    scheduleTokenRefresh();
  }, [apiBaseUrl, scheduleTokenRefresh]);

  const logout = useCallback(() => {
    if (user) {
      logUserLogout(user.id);
    }

    clearRefreshTimer();
    setUser(null);

    setAuditContext(null);

    fetch(`${apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      // Ignore logout errors - we've already cleared local state
    });
  }, [apiBaseUrl, clearRefreshTimer, user]);

  useEffect(() => {
    const handleSessionExpired = () => {
      clearRefreshTimer();
      setUser(null);
      setAuditContext(null);
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, [clearRefreshTimer]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/auth/me`, {
          credentials: 'include',
        });

        if (response.ok) {
          const data: { user: User } = await response.json();
          setUser(data.user);

          setAuditContext({
            userId: data.user.id,
          });

          scheduleTokenRefresh();
        } else if (response.status === 403) {
          try {
            await refreshToken();
          } catch {
            // Refresh failed, user needs to log in again
          }
        }
      } catch {
        // Network error - user is not authenticated
      }

      setIsInitialized(true);
    };

    initializeAuth();

    return () => {
      clearRefreshTimer();
    };
  }, [apiBaseUrl, refreshToken, scheduleTokenRefresh, clearRefreshTimer]);

  const contextValue: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    refreshToken,
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useIsAuthenticated(): boolean {
  const { isAuthenticated } = useAuth();
  return isAuthenticated;
}

export { AuthContext };
